import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { apiCreateInvite, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, AlertCircle, CheckCircle2 } from "lucide-react";

function rolesFor(actorRole) {
    if (actorRole === "Super Admin")
        return ["Admin", "Project Manager", "Team Lead", "Consultant", "Client"];
    if (actorRole === "Admin")
        return ["Admin", "Project Manager", "Team Lead", "Consultant", "Client"];
    if (actorRole === "Project Manager") return ["Team Lead", "Consultant"];
    if (actorRole === "Team Lead") return ["Consultant"];
    return [];
}

export default function InviteUserDialog({ open, onOpenChange, actorRole }) {
    const options = rolesFor(actorRole);
    const [email, setEmail] = useState("");
    const [fullName, setFullName] = useState("");
    const defaultRole = options.includes("Consultant")
        ? "Consultant"
        : options[options.length - 1] || "Consultant";
    const [role, setRole] = useState(defaultRole);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);

    function reset() {
        setEmail("");
        setFullName("");
        setRole(defaultRole);
        setError("");
        setResult(null);
        setSubmitting(false);
    }

    async function submit(e) {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            const res = await apiCreateInvite(
                email.trim().toLowerCase(),
                fullName.trim(),
                role,
            );
            const link = `${window.location.origin}/accept-invite/${res.invite_token}`;
            setResult({ ...res, link });
            toast.success("Invite created");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    }

    async function copy(text) {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Copied to clipboard");
        } catch {
            toast.error("Could not copy");
        }
    }

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                if (!v) reset();
                onOpenChange(v);
            }}
        >
            <DialogContent
                className="sm:max-w-[520px]"
                data-testid="invite-dialog"
            >
                <DialogHeader>
                    <DialogTitle>Invite a user</DialogTitle>
                    <DialogDescription>
                        The invited user will receive a link to set their own
                        password.
                    </DialogDescription>
                </DialogHeader>

                {!result ? (
                    <form
                        onSubmit={submit}
                        className="space-y-4"
                        data-testid="invite-user-form"
                    >
                        <div>
                            <Label htmlFor="inv-full" className="text-[12px] text-ntt-muted mb-1.5 block">
                                Full name
                            </Label>
                            <Input
                                id="inv-full"
                                required
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                placeholder="Ada Lovelace"
                                data-testid="invite-fullname-input"
                                className="h-11"
                            />
                        </div>
                        <div>
                            <Label htmlFor="inv-email" className="text-[12px] text-ntt-muted mb-1.5 block">
                                Email
                            </Label>
                            <Input
                                id="inv-email"
                                required
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="ada@company.com"
                                data-testid="invite-email-input"
                                className="h-11"
                            />
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                Role
                            </Label>
                            <Select value={role} onValueChange={setRole}>
                                <SelectTrigger
                                    className="h-11"
                                    data-testid="invite-role-trigger"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {options.map((r) => (
                                        <SelectItem
                                            key={r}
                                            value={r}
                                            data-testid={`invite-role-option-${r.replace(/\s+/g, "-").toLowerCase()}`}
                                        >
                                            {r}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-[11px] text-ntt-muted mt-1">
                                Default is Consultant. Role can be changed
                                later.
                            </p>
                        </div>
                        {error && (
                            <div
                                data-testid="invite-user-error"
                                className="flex items-start gap-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                            >
                                <AlertCircle className="h-4 w-4 mt-[1px] shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}
                        <DialogFooter className="pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                data-testid="invite-cancel-btn"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={submitting}
                                data-testid="invite-submit-btn-dialog"
                                className="bg-[#0072BC] hover:bg-[#005a94] text-white"
                            >
                                {submitting ? "Creating…" : "Create invite"}
                            </Button>
                        </DialogFooter>
                    </form>
                ) : (
                    <div className="space-y-4" data-testid="invite-result">
                        <div className="flex items-center gap-2 text-[13px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                            <CheckCircle2 className="h-4 w-4" />
                            Invite created. Share this link with{" "}
                            <span className="font-medium">{result.user.email}</span>.
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                Invite link
                            </Label>
                            <div className="flex gap-2">
                                <Input
                                    readOnly
                                    value={result.link}
                                    data-testid="invite-link-value"
                                    className="h-11 font-mono text-[12px]"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => copy(result.link)}
                                    data-testid="invite-copy-btn"
                                    className="h-11"
                                >
                                    <Copy className="h-4 w-4 mr-1" />
                                    Copy
                                </Button>
                            </div>
                            <p className="text-[11px] text-ntt-muted mt-1">
                                Link expires in {result.invite_expires_in_days}{" "}
                                days.
                            </p>
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                onClick={reset}
                                variant="outline"
                                data-testid="invite-another-btn"
                            >
                                Invite another
                            </Button>
                            <Button
                                type="button"
                                onClick={() => onOpenChange(false)}
                                data-testid="invite-done-btn"
                                className="bg-[#0072BC] hover:bg-[#005a94] text-white"
                            >
                                Done
                            </Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
